import {
  Flex,
  Text,
  Box,
  Badge,
  IconButton,
  Tooltip,
  Link,
} from '@chakra-ui/react';
import { Icons } from '../Icons';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { useTranslation } from 'react-i18next';
import { externalLinks } from '@texas/externalLinks';
import { ReactElement } from 'react';
import {
  BranchIdentifier,
  getBranchColor,
} from '@texas/utils/helpers/texas/branchHelpers';

type AvatarSize = 'default' | 'sm';

const sizeConfig: Record<
  AvatarSize,
  {
    iconBoxSize: number;
    nameFontSize: string;
    labelFontSize: string;
    padding: number;
    fontWeight: string;
  }
> = {
  default: {
    iconBoxSize: 8,
    nameFontSize: 'md',
    labelFontSize: 'xs',
    padding: 1,
    fontWeight: 'bold',
  },
  sm: {
    iconBoxSize: 5,
    nameFontSize: 'sm',
    labelFontSize: '2xs',
    fontWeight: 'medium',
    padding: 0.5,
  },
};

interface Props {
  name: string;
  email: string;
  label: string | null;
  showMainBadge?: boolean;
  buttons?: ReactElement;
  showTeams: boolean;
  branch?: BranchIdentifier;
  size?: AvatarSize;
}

export function UserAvatar({
  name,
  label,
  showMainBadge,
  buttons,
  email,
  showTeams,
  branch,
  size = 'default',
}: Props) {
  const { iconBoxSize, nameFontSize, labelFontSize, padding, fontWeight } =
    sizeConfig[size];
  const { t } = useTranslation();

  return (
    <Flex
      align="center"
      gap={2}
      minW="150px"
      w="fit-content"
      pos="relative"
      animation={fadeInScaleAnimation()}
      sx={{
        ':hover > .utils': {
          opacity: 1,
        },
        ':hover > .user': {
          maskImage: 'linear-gradient(to right, #ffffff90, transparent)',
          maskClip: 'no-clip',
        },
      }}
    >
      <Flex
        align="center"
        className="user"
        gap={2}
        transition="mask-image 200ms ease"
      >
        <Box pos="relative">
          <Box
            bg="white"
            _light={{
              bg: branch ? getBranchColor(branch) : 'gray.900',
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={padding}
            borderRadius="full"
          >
            <Icons.AccountCircle
              boxSize={iconBoxSize}
              color={branch ? getBranchColor(branch) : 'black'}
              _light={{ color: 'white' }}
            />
          </Box>
        </Box>
        <Flex flexDir="column" align="start">
          <Flex align="center" gap={1}>
            <Text fontWeight={fontWeight} fontSize={nameFontSize}>
              {name}
            </Text>
            {showMainBadge && (
              <Badge
                animation={fadeInScaleAnimation()}
                bg="texas.sand.100"
                color="white"
                fontSize="2xs"
                lineHeight="normal"
                px={1}
                py={0}
                _light={{ bg: 'texas.burntSienna.500' }}
              >
                {t('general.main')}
              </Badge>
            )}
          </Flex>
          <Text fontSize={labelFontSize} variant="sub">
            {label}
          </Text>
        </Flex>
      </Flex>
      <Box
        opacity={0}
        transition="opacity 100ms ease"
        pos="absolute"
        right={-4}
        minW="50%"
        top={-2}
        bottom={-4}
        pr={4}
        className="utils"
      >
        <Flex
          gap={2}
          h="full"
          paddingLeft="42px"
          alignItems="center"
          justifyContent="end"
        >
          {showTeams && (
            <Tooltip label={t('user.chat')}>
              <IconButton
                as={Link}
                variant="avatar-badge"
                icon={<Icons.Chat />}
                aria-label="Chat"
                target="_blank"
                href={externalLinks.microsoftTeams(email)}
              />
            </Tooltip>
          )}
          {email.length > 0 && (
            <Tooltip label={t('user.email')}>
              <IconButton
                as={Link}
                variant="avatar-badge"
                icon={<Icons.Email />}
                aria-label={t('general.sendEmail')}
                href={externalLinks.mailTo(email)}
              />
            </Tooltip>
          )}
          {buttons && <>{buttons}</>}
        </Flex>
      </Box>
    </Flex>
  );
}
